import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Plus, ExternalLink, Sparkles, AlertCircle, RefreshCw, Cloud } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Tickets() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [servicenowTickets, setServicenowTickets] = useState([]);
  const [configuredSystems, setConfiguredSystems] = useState([]);
  const [assignedAgentsMap, setAssignedAgentsMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [servicenowLoading, setServicenowLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('servicenow');
  const [syncing, setSyncing] = useState(false);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    ticket_number: '',
    source: 'servicenow',
    title: '',
    description: '',
    priority: 'medium',
    requester_name: '',
    requester_email: '',
    sap_system: 'S/4HANA Cloud',
    target_system_id: '',
    target_system_name: '',
    customization_type: 'Enhancement',
    is_error_ticket: false,
    error_code: '',
    error_message: '',
    error_module: ''
  });

  useEffect(() => {
    fetchTickets();
    fetchConfiguredSystems();
    fetchServicenowTickets();
  }, []);

  const fetchConfiguredSystems = async () => {
    try {
      const response = await axios.get(`${API}/settings/sap-systems`);
      setConfiguredSystems(response.data);
    } catch (error) {
      console.error('Error fetching configured systems:', error);
    }
  };

  const fetchTickets = async () => {
    try {
      const response = await axios.get(`${API}/tickets`);
      setTickets(response.data);
      
      // Fetch assigned agents for each ticket
      const agentPromises = response.data.map(async (ticket) => {
        try {
          const agentsRes = await axios.get(`${API}/agent-studio/tickets/${ticket.id}/agents`);
          return { ticketId: ticket.id, agents: agentsRes.data };
        } catch (err) {
          return { ticketId: ticket.id, agents: [] };
        }
      });
      
      const agentsData = await Promise.all(agentPromises);
      const agentsMap = {};
      agentsData.forEach(({ ticketId, agents }) => {
        agentsMap[ticketId] = agents;
      });
      setAssignedAgentsMap(agentsMap);
      
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchServicenowTickets = async () => {
    setServicenowLoading(true);
    try {
      const response = await axios.get(`${API}/servicenow/incidents`);
      setServicenowTickets(response.data);
    } catch (error) {
      console.error('Error fetching ServiceNow tickets:', error);
      toast.error('Failed to fetch ServiceNow tickets');
    } finally {
      setServicenowLoading(false);
    }
  };

  const syncServicenowTickets = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/servicenow/sync`);
      toast.success(`Synced ${response.data.new_tickets} new tickets, updated ${response.data.updated_tickets}`);
      // Refresh both lists
      await fetchTickets();
      await fetchServicenowTickets();
    } catch (error) {
      console.error('Error syncing ServiceNow tickets:', error);
      toast.error('Failed to sync ServiceNow tickets');
    } finally {
      setSyncing(false);
    }
  };

  const createMockTicket = async () => {
    try {
      const response = await axios.post(`${API}/tickets/mock`);
      console.log('Mock ticket created:', response.data);
      alert(`✅ Mock ticket created: ${response.data.ticket_number}`);
      await fetchTickets();
    } catch (error) {
      console.error('Error creating mock ticket:', error);
      alert(`❌ Error creating mock ticket: ${error.response?.data?.detail || error.message}`);
    }
  };

  const createTicket = async () => {
    try {
      await axios.post(`${API}/tickets`, formData);
      setOpen(false);
      fetchTickets();
      setFormData({
        ticket_number: '',
        source: 'servicenow',
        title: '',
        description: '',
        priority: 'medium',
        requester_name: '',
        requester_email: '',
        sap_system: 'S/4HANA Cloud',
        target_system_id: '',
        target_system_name: '',
        customization_type: 'Enhancement'
      });
    } catch (error) {
      console.error('Error creating ticket:', error);
    }
  };

  const handleSystemSelection = (systemId) => {
    const selectedSystem = configuredSystems.find(s => s.id === systemId);
    setFormData({
      ...formData,
      target_system_id: systemId,
      target_system_name: selectedSystem ? selectedSystem.name : '',
      sap_system: selectedSystem ? selectedSystem.type : formData.sap_system
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      'open': 'bg-gray-500',
      'in_analysis': 'bg-blue-500',
      'in_design': 'bg-purple-500',
      'in_development': 'bg-indigo-500',
      'in_testing': 'bg-amber-500',
      'in_deployment': 'bg-orange-500',
      'in_governance': 'bg-cyan-500',
      'completed': 'bg-green-500',
      'rejected': 'bg-red-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'low': 'border-gray-400 text-gray-600',
      'medium': 'border-blue-400 text-blue-600',
      'high': 'border-orange-400 text-orange-600',
      'critical': 'border-red-400 text-red-600'
    };
    return colors[priority] || 'border-gray-400 text-gray-600';
  };

  if (loading) {
    return <div className="flex items-center justify-center h-full"><div className="text-lg">Loading...</div></div>;
  }

  return (
    <div className="space-y-6" data-testid="tickets-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Tickets</h1>
          <p className="text-muted-foreground mt-2">Manage customization requests from ServiceNow</p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={fetchServicenowTickets} 
            variant="outline" 
            disabled={servicenowLoading}
            data-testid="refresh-servicenow-btn"
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${servicenowLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="servicenow" className="flex items-center gap-2">
            <Cloud className="h-4 w-4" />
            ServiceNow ({servicenowTickets.length})
          </TabsTrigger>
          <TabsTrigger value="local" className="flex items-center gap-2">
            Local ({tickets.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="servicenow" className="mt-4">
          {servicenowLoading ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground mb-4" />
                <p className="text-muted-foreground">Loading ServiceNow tickets...</p>
              </CardContent>
            </Card>
          ) : servicenowTickets.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Cloud className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground mb-4">No ServiceNow tickets found.</p>
                <Button onClick={fetchServicenowTickets} variant="outline">
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Refresh from ServiceNow
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {[...servicenowTickets]
                .sort((a, b) => {
                  // Sort by ticket_number descending (INC0101461 > INC0101456)
                  const numA = parseInt(a.ticket_number.replace(/\D/g, '')) || 0;
                  const numB = parseInt(b.ticket_number.replace(/\D/g, '')) || 0;
                  return numB - numA;
                })
                .map((ticket) => (
                <Card 
                  key={ticket.id} 
                  className="hover:shadow-lg transition-shadow cursor-pointer" 
                  onClick={() => {
                    // Navigate directly to ServiceNow ticket detail page using sys_id
                    navigate(`/servicenow-tickets/${ticket.servicenow_sys_id}`);
                  }}
                  data-testid={`servicenow-ticket-${ticket.ticket_number}`}
                >
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <Badge variant="outline" className="font-mono text-xs bg-blue-50">{ticket.ticket_number}</Badge>
                          <Badge variant="outline" className="text-xs bg-green-50">
                            <Cloud className="h-3 w-3 mr-1" />
                            ServiceNow
                          </Badge>
                          <Badge className={`text-xs ${getStatusColor(ticket.status)}`}>
                            {ticket.status.replace('_', ' ')}
                          </Badge>
                          <Badge variant="outline" className={`text-xs border ${getPriorityColor(ticket.priority)}`}>
                            {ticket.priority}
                          </Badge>
                          {ticket.category && (
                            <Badge variant="secondary" className="text-xs">
                              {ticket.category}
                            </Badge>
                          )}
                        </div>
                        <CardTitle className="text-xl">{ticket.title}</CardTitle>
                        <CardDescription className="mt-2 line-clamp-2">{ticket.description}</CardDescription>
                      </div>
                      <ExternalLink className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-4 gap-4 text-sm">
                      <div>
                        <div className="text-muted-foreground">System</div>
                        <div className="font-medium">{ticket.sap_system}</div>
                      </div>
                      <div>
                        <div className="text-muted-foreground">Type</div>
                        <div className="font-medium">{ticket.customization_type}</div>
                      </div>
                      <div>
                        <div className="text-muted-foreground">Requester</div>
                        <div className="font-medium">{ticket.requester_name || 'Unknown'}</div>
                      </div>
                      <div>
                        <div className="text-muted-foreground">Created</div>
                        <div className="font-medium">{new Date(ticket.created_at).toLocaleDateString()}</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="local" className="mt-4">
          {tickets.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <p className="text-muted-foreground mb-4">No local tickets found. Sync from ServiceNow or create a new ticket.</p>
                <div className="flex gap-2">
                  <Button onClick={syncServicenowTickets} variant="outline">
                    <Cloud className="mr-2 h-4 w-4" />
                    Sync from ServiceNow
                  </Button>
                  <Button onClick={createMockTicket}>Generate Mock Ticket</Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {tickets.map((ticket) => (
                <Card key={ticket.id} className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate(`/tickets/${ticket.id}`)} data-testid={`ticket-card-${ticket.id}`}>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="outline" className="font-mono text-xs">{ticket.ticket_number}</Badge>
                          <Badge variant="outline" className="text-xs">{ticket.source}</Badge>
                          <Badge className={`text-xs ${getStatusColor(ticket.status)}`}>{ticket.status.replace('_', ' ')}</Badge>
                          <Badge variant="outline" className={`text-xs border ${getPriorityColor(ticket.priority)}`}>{ticket.priority}</Badge>
                        </div>
                        <CardTitle className="text-xl">{ticket.title}</CardTitle>
                        <CardDescription className="mt-2">{ticket.description}</CardDescription>
                      </div>
                      <ExternalLink className="h-5 w-5 text-muted-foreground" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="grid grid-cols-4 gap-4 text-sm">
                        <div>
                          <div className="text-muted-foreground">Target System</div>
                          <div className="font-medium">
                            {ticket.target_system_name || ticket.sap_system}
                          </div>
                        </div>
                        <div>
                          <div className="text-muted-foreground">Type</div>
                          <div className="font-medium">{ticket.customization_type}</div>
                        </div>
                        <div>
                          <div className="text-muted-foreground">Requester</div>
                          <div className="font-medium">{ticket.requester_name}</div>
                        </div>
                        <div>
                          <div className="text-muted-foreground">Created</div>
                          <div className="font-medium">{new Date(ticket.created_at).toLocaleDateString()}</div>
                        </div>
                      </div>
                      
                      {/* Assigned Agents from Agent Studio */}
                      {assignedAgentsMap[ticket.id] && assignedAgentsMap[ticket.id].length > 0 && (
                        <div className="pt-2 border-t">
                          <div className="text-xs text-muted-foreground mb-1">Custom Agents:</div>
                          <div className="flex flex-wrap gap-1">
                            {assignedAgentsMap[ticket.id].map((assignment, idx) => (
                              <Badge key={idx} variant="secondary" className="text-xs">
                                <Sparkles className="h-3 w-3 mr-1" />
                                {assignment.agent_name}
                                {assignment.status === 'completed' && ' ✓'}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
